import { useState } from "react";
import { toast } from "sonner";

import { BottomSheet, ChalkActionButton, ChalkCard, SectionTitle } from "../chalk/primitives";
import { tournaments } from "../../lib/tournamentApi";
import PoolsPanel from "./PoolsPanel";
import { fmtSlot, inputStyle, pillBtn, roundLabel, teamName } from "./shared";

export function groupRounds(games) {
  const rounds = {};
  for (const g of games) (rounds[g.round] = rounds[g.round] || []).push(g);
  return { rounds, roundKeys: Object.keys(rounds).map(Number).sort((a, b) => a - b) };
}

export const STAGES = [
  ["pool", "Pools"], ["main", "Bracket"], ["winners", "Winners"], ["losers", "Losers"], ["final", "Final"],
  ["third_place", "3rd place"], ["consolation", "Consolation"], ["silver", "Silver"],
];
export const stageOf = (g) => (g.stage === "grand_final" || g.stage === "if_necessary" ? "final" : g.stage || "main");
export function stageLabel(key, division) {
  if (key === "main" && division?.format === "pools") return "Gold";
  return STAGES.find(([k]) => k === key)?.[1] || key;
}
export function decidedText(g) {
  if (!g.decided_by) return "";
  return `Won ${g.home_score}–${g.away_score}, ${g.decided_by}`;
}

export default function BracketTab({ data, division, isDirector, onChanged }) {
  const t = data.tournament;
  const games = data.games.filter((g) => g.division_id === division.id);
  const present = STAGES.map(([k]) => k).filter((k) => games.some((g) => stageOf(g) === k) || (k === "pool" && division.format === "pools"));
  const [stage, setStage] = useState(null);
  const [view, setView] = useState("list");
  const [editing, setEditing] = useState(null);
  const teamsById = Object.fromEntries(data.teams.map((x) => [x.id, x]));
  const active = present.includes(stage) ? stage : present[present.length > 1 && present[0] === "pool" && division.stage === "bracket" ? 1 : 0];
  const consPending = division.consolation === "first_game" && division.bracket_locked && !games.some((g) => g.stage === "consolation");

  if (!present.length) {
    return <ChalkCard data-testid="bracket-empty"><div style={{ textAlign: "center", padding: 20, color: "var(--chalk-muted)" }}>
      Add teams on the Teams tab, then Build bracket.</div></ChalkCard>;
  }
  async function reset() {
    if (!window.confirm(`Reset ${division.name}? All games, scores and schedule slots in it are cleared.`)) return;
    try { await tournaments.resetDivision(t.id, division.id); toast.success("Reset."); await onChanged(); }
    catch (e) { toast.error(e.message || "Couldn't reset."); }
  }
  const list = games.filter((g) => stageOf(g) === active);
  return (
    <div data-testid="tournament-bracket">
      <div style={{ display: "flex", gap: 4, marginBottom: 8, overflowX: "auto" }} data-testid="stage-tabs">
        {present.map((k) => (
          <button key={k} data-testid={`stage-tab-${k}`} onClick={() => setStage(k)} style={pillBtn(active === k, "var(--green-mid)")}>
            {stageLabel(k, division)}
          </button>
        ))}
      </div>
      {consPending && <div data-testid="consolation-pending" style={{ fontSize: 12, color: "var(--chalk-muted)", marginBottom: 8 }}>
        Consolation bracket set after Round 2 (once every team's first game is final).</div>}
      <div style={{ display: "flex", gap: 4, marginBottom: 8, alignItems: "center" }}>
        {["main", "winners"].includes(active) && ["list", "svg"].map((v) => (
          <button key={v} onClick={() => setView(v)} data-testid={`bracket-view-${v}`} style={pillBtn(view === v)}>
            {v === "list" ? "Games" : "Bracket"}</button>
        ))}
        {isDirector && division.bracket_locked && (
          <button onClick={reset} data-testid="bracket-reset" style={{ marginLeft: "auto", background: "transparent", border: "none",
                                                                       color: "var(--chalk-red)", fontSize: 12, cursor: "pointer" }}>Reset division</button>
        )}
      </div>
      {active === "pool" ? (
        <PoolsPanel data={data} division={division} isDirector={isDirector} onChanged={onChanged} onEdit={setEditing} teamsById={teamsById} />
      ) : view === "svg" && ["main", "winners"].includes(active) ? (
        <BracketSVG games={list} teamsById={teamsById} onGameClick={(g) => !g.is_bye && setEditing(g)} />
      ) : (
        <RoundList games={list} stage={active} teamsById={teamsById} tz={t.timezone} onEdit={setEditing} />
      )}
      {editing && (
        <ScoreSheet game={editing} teamsById={teamsById} t={t} rules={division.rules || t.rules}
                    onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await onChanged(); }} />
      )}
    </div>
  );
}

function RoundList({ games, stage, teamsById, tz, onEdit }) {
  const { rounds, roundKeys } = groupRounds(games);
  const max = roundKeys[roundKeys.length - 1];
  const label = (r) => (stage === "final" ? (rounds[r][0].stage === "if_necessary" ? "If necessary" : "Grand final")
    : stage === "losers" ? `Losers round ${roundKeys.indexOf(r) + 1}` : stage === "third_place" ? "3rd place" : roundLabel(r, max));
  return roundKeys.map((r) => (
    <div key={r} style={{ marginBottom: 12 }}>
      <SectionTitle label={label(r)} />
      {rounds[r].map((g) => <GameRow key={g.id} game={g} teamsById={teamsById} tz={tz} onClick={() => !g.is_bye && g.status !== "not_needed" && onEdit(g)} />)}
    </div>
  ));
}

export function GameRow({ game, teamsById, tz, onClick }) {
  const slot = [game.court, fmtSlot(game.scheduled_at || game.starts_at, tz)].filter(Boolean).join(" · ");
  const side = (s) => (
    <div style={{ display: "flex", justifyContent: "space-between", fontWeight: game.winner_team_id && game.winner_team_id === game[`${s}_team_id`] ? 700 : 400 }}>
      <span>{game.is_bye && !game[`${s}_team_id`] ? "Bye" : teamName(teamsById, game[`${s}_team_id`])}</span>
      <span>{game[`${s}_score`] ?? "—"}</span>
    </div>
  );
  return (
    <ChalkCard data-testid={`game-row-${game.id}`} onClick={onClick}
               style={{ marginBottom: 6, cursor: game.is_bye ? "default" : "pointer", opacity: game.is_bye || game.status === "not_needed" ? 0.55 : 1 }}>
      {side("home")}{side("away")}
      <div style={{ fontSize: 10, color: "var(--chalk-muted)", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {slot || "Unscheduled"} · {game.is_bye ? "bye" : game.status === "not_needed" ? "not needed" : game.status}
        {game.decided_by && <span data-testid={`decided-${game.id}`}> · {decidedText(game)}</span>}
        {game.set_scores?.length > 0 && <span> · {game.set_scores.map((s) => s.join("-")).join(", ")}</span>}
        {game.edited_after_final && <span style={{ color: "#B07B00" }}> · edited</span>}
      </div>
    </ChalkCard>
  );
}

function BracketSVG({ games, teamsById, onGameClick }) {
  const { rounds, roundKeys } = groupRounds(games);
  const COL_W = 160, ROW_H = 56, GAP = 18;
  const H = Math.max(...roundKeys.map((r) => rounds[r].length)) * (ROW_H + GAP) + 40;
  return (
    <div style={{ overflowX: "auto", background: "var(--chalk-board)", borderRadius: 8, padding: 10 }}>
      <svg data-testid="bracket-svg" width={roundKeys.length * COL_W} height={H}>
        {roundKeys.map((r, ri) => rounds[r].map((g, gi) => {
          const x = ri * COL_W + 8, y = gi * (H / rounds[r].length) + 10;
          return (
            <g key={g.id} onClick={() => onGameClick(g)} style={{ cursor: "pointer" }}>
              <rect x={x} y={y} width={COL_W - 16} height={ROW_H} fill="#F5F0E8" stroke="#2E7D52" strokeWidth="0.5" rx="4" />
              <text x={x + 6} y={y + 18} fontSize="10" fill="#1C2B1E">{teamName(teamsById, g.home_team_id).slice(0, 18)} {g.home_score ?? ""}</text>
              <text x={x + 6} y={y + 40} fontSize="10" fill="#1C2B1E">{teamName(teamsById, g.away_team_id).slice(0, 18)} {g.away_score ?? ""}</text>
            </g>
          );
        }))}
      </svg>
    </div>
  );
}

function ScoreSheet({ game, teamsById, t, rules, onClose, onSaved }) {
  const [hs, setHs] = useState(game.home_score ?? "");
  const [as_, setAs] = useState(game.away_score ?? "");
  const [sets, setSets] = useState((game.set_scores || []).map((s) => s.join("-")).join(", "));
  const [winner, setWinner] = useState(game.winner_team_id || "");
  const [method, setMethod] = useState(game.decided_by || rules?.tie_methods?.[0] || "Director decision");
  const [court, setCourt] = useState(game.court || "");
  const [busy, setBusy] = useState(false);
  const [confirmReset, setConfirmReset] = useState(null);
  const hub = t.tier === "hub";
  const tied = hs !== "" && as_ !== "" && Number(hs) === Number(as_);
  const needsDecision = tied && !(game.stage === "pool" && rules?.ties_allowed);
  const sport = rules?.sport;

  async function save({ status = null, force = false, doubleForfeit = false }) {
    setBusy(true);
    try {
      const setScores = sets.trim() ? sets.split(",").map((p) => p.trim().split(/[-–]/).map(Number)).filter((p) => p.length === 2 && p.every((n) => !Number.isNaN(n))) : undefined;
      await tournaments.patchGame(t.id, game.id, {
        home_score: hs === "" ? null : Number(hs), away_score: as_ === "" ? null : Number(as_),
        status, force, double_forfeit: doubleForfeit, expected_version: game.version,
        ...(hub ? {} : { court: court || null }),
        ...(needsDecision && status === "final" ? { winner_team_id: winner || null, decided_by: method } : {}),
        ...(setScores ? { set_scores: setScores } : {}),
      });
      toast.success(status === "final" ? "Final." : "Saved.");
      onSaved();
    } catch (e) {
      if (e.code === "BRACKET_DOWNSTREAM_LOCKED" && e.meta?.would_reset) setConfirmReset(e.meta.would_reset);
      else if (e.code === "GAME_CHANGED") toast.error("Another helper changed this game. Review and try again.");
      else toast.error(e.message || "Couldn't save.");
    } finally { setBusy(false); }
  }
  return (
    <BottomSheet isOpen title={`Score · ${rules?.score_label || "Points"}`} onDismiss={onClose}>
      <div data-testid="score-sheet" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {[["home", hs, setHs], ["away", as_, setAs]].map(([side, val, set]) => (
          <div key={side} style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ flex: 1 }}>{teamName(teamsById, game[`${side}_team_id`])}</div>
            <input type="number" inputMode="numeric" value={val} onChange={(e) => set(e.target.value)}
                   data-testid={`score-${side}`} style={_scoreInput} />
          </div>
        ))}
        {!hub && (
          <input value={court} onChange={(e) => setCourt(e.target.value)} placeholder="Location"
                 data-testid="score-location" style={inputStyle} />
        )}
        {sport === "volleyball" && (
          <input value={sets} onChange={(e) => setSets(e.target.value)} placeholder="Set scores (optional): 25-20, 22-25, 15-10"
                 data-testid="score-sets" style={inputStyle} />
        )}
        {needsDecision && (
          <div data-testid="tie-decision" style={{ background: "var(--chalk-amber-bg)", borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Tied — who won, and how?</div>
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              {["home", "away"].map((s) => (
                <button key={s} data-testid={`tie-winner-${s}`} onClick={() => setWinner(game[`${s}_team_id`])}
                        style={pillBtn(winner === game[`${s}_team_id`], "var(--green-mid)")}>{teamName(teamsById, game[`${s}_team_id`])}</button>
              ))}
            </div>
            <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="tie-method" style={inputStyle}>
              {(rules?.tie_methods || ["Director decision"]).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )}
        <ChalkActionButton variant="primary" label="FINAL" loading={busy} testId="score-final" onClick={() => save({ status: "final" })} />
        <div style={{ display: "flex", gap: 6 }}>
          <ChalkActionButton label="Save" loading={busy} fullWidth={false} onClick={() => save({})} testId="score-save" />
          <ChalkActionButton label="Double forfeit" fullWidth={false} testId="score-double-forfeit"
                             onClick={() => save({ doubleForfeit: true, status: "forfeit" })} />
        </div>
        {confirmReset && (
          <div style={{ background: "#FBE3B4", padding: 10, borderRadius: 8 }}>
            <div style={{ fontSize: 12, color: "#5A3A00" }}>This change resets {confirmReset.length} downstream game(s). Confirm?</div>
            <ChalkActionButton label="Yes, cascade" variant="primary" testId="score-cascade-confirm"
                               onClick={() => { setConfirmReset(null); save({ status: "final", force: true }); }} />
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

const _scoreInput = { width: 70, padding: "8px 10px", borderRadius: 8, border: "0.5px solid var(--chalk-border)",
  background: "#FBF8F2", color: "var(--chalk-board)", fontSize: 18, fontWeight: 700, textAlign: "center" };
